/**
 * Domain-flavoured presentational pieces shared by web and desktop:
 * task priority, task status and client status chips, plus the branding hook
 * that applies a tenant's White Label palette at runtime.
 */
import { useEffect } from 'react';
import { cn } from '../lib/cn';
import { Badge, type BadgeTone } from './primitives';

export type Priority = 'high' | 'medium' | 'low';

const PRIORITY_LABEL: Record<Priority, string> = {
  high: 'Високий',
  medium: 'Середній',
  low: 'Низький',
};

const PRIORITY_DOT: Record<Priority, string> = {
  high: 'bg-priority-high',
  medium: 'bg-priority-medium',
  low: 'bg-priority-low',
};

const PRIORITY_TONE: Record<Priority, BadgeTone> = {
  high: 'danger',
  medium: 'warning',
  low: 'info',
};

export function PriorityDot({ priority, className }: { priority: Priority; className?: string }) {
  return (
    <span
      className={cn('inline-block h-2 w-2 shrink-0 rounded-full', PRIORITY_DOT[priority], className)}
      title={PRIORITY_LABEL[priority]}
      aria-label={PRIORITY_LABEL[priority]}
    />
  );
}

export function PriorityBadge({ priority }: { priority: Priority }) {
  return (
    <Badge tone={PRIORITY_TONE[priority]}>
      <PriorityDot priority={priority} />
      {PRIORITY_LABEL[priority]}
    </Badge>
  );
}

export type TaskStatus =
  | 'backlog'
  | 'planned'
  | 'in_progress'
  | 'blocked'
  | 'done'
  | 'not_done';

const STATUS_META: Record<TaskStatus, { label: string; tone: BadgeTone }> = {
  backlog: { label: 'Беклог', tone: 'neutral' },
  planned: { label: 'Заплановано', tone: 'info' },
  in_progress: { label: 'В роботі', tone: 'accent' },
  blocked: { label: 'Заблоковано', tone: 'warning' },
  done: { label: 'Виконано', tone: 'success' },
  not_done: { label: 'Не виконано', tone: 'danger' },
};

export function StatusBadge({ status }: { status: TaskStatus }) {
  const meta = STATUS_META[status] ?? STATUS_META.planned;
  return <Badge tone={meta.tone}>{meta.label}</Badge>;
}

const CLIENT_STATUS_META: Record<string, { label: string; tone: BadgeTone }> = {
  lead: { label: 'Лід', tone: 'info' },
  in_progress: { label: 'В роботі', tone: 'accent' },
  negotiation: { label: 'Переговори', tone: 'warning' },
  customer: { label: 'Клієнт', tone: 'success' },
  dormant: { label: 'Сплячий', tone: 'neutral' },
  lost: { label: 'Втрачений', tone: 'danger' },
};

export function ClientStatusBadge({ status }: { status: string }) {
  const meta = CLIENT_STATUS_META[status] ?? { label: status, tone: 'neutral' as BadgeTone };
  return <Badge tone={meta.tone}>{meta.label}</Badge>;
}

export interface Branding {
  product_name?: string;
  logo_url?: string;
  favicon_url?: string;
  primary_color?: string;
  accent_color?: string;
  login_background_url?: string;
  support_email?: string;
  hide_vendor_branding?: boolean;
}

/** Convert `#1f9d71` to the `H S% L%` triplet the tokens expect. */
export function hexToHslTriplet(hex: string): string | null {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return null;
  const int = parseInt(match[1], 16);
  const r = ((int >> 16) & 255) / 255;
  const g = ((int >> 8) & 255) / 255;
  const b = (int & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Applies tenant branding: accent tokens on `:root`, title and favicon.
 *
 * Done at runtime rather than at build time because one deployment serves
 * many tenants, and an On-Premise customer should be able to rename the
 * product from the settings screen instead of from a rebuild.
 */
export function useBranding(branding: Branding | null | undefined): void {
  useEffect(() => {
    if (!branding) return;
    const root = document.documentElement;
    const accent = branding.primary_color ?? branding.accent_color;
    const triplet = accent ? hexToHslTriplet(accent) : null;
    if (triplet) {
      root.style.setProperty('--z-accent', triplet);
      root.style.setProperty('--z-ring', triplet);
    }
    if (branding.product_name) document.title = branding.product_name;

    // The tab icon is half of what makes a rebranded app feel rebranded, and
    // the original is restored on cleanup so a sign-out does not leave the
    // previous tenant's mark behind.
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    const originalIcon = link?.getAttribute('href') ?? null;
    if (link && branding.favicon_url) link.href = branding.favicon_url;

    return () => {
      root.style.removeProperty('--z-accent');
      root.style.removeProperty('--z-ring');
      if (link && originalIcon !== null) link.href = originalIcon;
    };
  }, [branding]);
}
